import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './auth';
import {
  createPlaylist,
  listMyPlaylists,
  listSharedPlaylists,
  renamePlaylist,
  deletePlaylist,
} from './playlists';
import { editSharedPlaylist } from './api';
import { formatRelativeTime } from './ui';
import { ItemCard } from './item-card';
import { ConfirmIconAction } from './quick-actions';
import { SelectionModeToggle, SelectionBar, useSelection } from './list-controls';
import { mapWithConcurrency } from './concurrency';
import { Playlist } from './types';

type Tab = 'mine' | 'shared';

export function PlaylistsPage() {
  const { identityId } = useAuth();
  const [tab, setTab] = useState<Tab>('mine');
  const [newName, setNewName] = useState('');
  const [selectionMode, setSelectionMode] = useState(false);
  const [bulkSummary, setBulkSummary] = useState<string | null>(null);
  const [bulkPending, setBulkPending] = useState(false);
  const queryClient = useQueryClient();
  const selection = useSelection<Playlist>((p) => p.playlistId);

  const mine = useQuery({
    queryKey: ['playlists', 'mine', identityId],
    enabled: !!identityId && tab === 'mine',
    queryFn: () => listMyPlaylists(identityId!),
  });

  const shared = useQuery({
    queryKey: ['playlists', 'shared'],
    enabled: tab === 'shared',
    queryFn: () => listSharedPlaylists(),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => createPlaylist(identityId!, name),
    onSuccess: () => {
      setNewName('');
      queryClient.invalidateQueries({ queryKey: ['playlists', 'mine', identityId] });
    },
  });

  const active = tab === 'mine' ? mine : shared;
  const rows = active.data ?? [];
  const refetchActive = () => active.refetch();
  // Bulk delete is owner-only (matches the single-item rule) — on the "shared" tab most rows
  // belong to someone else, so only the caller's own playlists are ever selectable here.
  const ownedRows = rows.filter((p) => p.ownerId === identityId);

  async function handleBulkDelete() {
    const ids = [...selection.selectedIds];
    if (ids.length === 0) return;
    if (!confirm(`Delete ${ids.length} playlist${ids.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
    setBulkPending(true);
    setBulkSummary(null);
    const targets = rows.filter((p) => ids.includes(p.playlistId));
    const results = await mapWithConcurrency(targets, (p) => deletePlaylist(p.ownerId, p.playlistId), 8);
    const succeeded = results.filter((r) => r.status === 'fulfilled').map((r) => r.item.playlistId);
    const failed = results.filter((r) => r.status === 'rejected');
    if (succeeded.length > 0) succeeded.forEach((id) => selection.toggle(id));
    setBulkSummary(
      failed.length === 0
        ? `Deleted ${succeeded.length} playlist${succeeded.length === 1 ? '' : 's'}.`
        : `Deleted ${succeeded.length} of ${ids.length}. ${failed.length} couldn't be deleted — try again.`,
    );
    setBulkPending(false);
    refetchActive();
  }

  return (
    <div>
      <h1>Playlists</h1>
      <div
        className="tabs"
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 'var(--space-4)',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={tab === 'mine' ? '' : 'secondary'} onClick={() => setTab('mine')}>
            My playlists
          </button>
          <button className={tab === 'shared' ? '' : 'secondary'} onClick={() => setTab('shared')}>
            Shared playlists
          </button>
        </div>
        <SelectionModeToggle
          active={selectionMode}
          onToggle={() => {
            setSelectionMode((v) => !v);
            selection.clear();
            setBulkSummary(null);
          }}
        />
      </div>

      {tab === 'mine' && (
        <form
          className="card"
          style={{ marginBottom: 'var(--space-4)', display: 'flex', gap: 8 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (newName.trim()) createMutation.mutate(newName.trim());
          }}
        >
          <input
            placeholder="New playlist name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            style={{ flex: 1 }}
          />
          <button type="submit" disabled={!newName.trim() || createMutation.isPending}>
            Create
          </button>
        </form>
      )}

      {selectionMode && (
        <SelectionBar count={selection.count} onSelectAll={() => selection.selectAll(ownedRows)} onClear={selection.clear}>
          <button onClick={handleBulkDelete} disabled={selection.count === 0 || bulkPending}>
            Delete selected
          </button>
        </SelectionBar>
      )}
      {bulkSummary && <p className="muted">{bulkSummary}</p>}

      {active.isLoading ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center' }}>
          <p className="muted">
            {tab === 'mine' ? "You haven't created any playlists yet." : 'No shared playlists yet.'}
          </p>
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {rows.map((p) => (
            <PlaylistListItem
              key={p.playlistId}
              playlist={p}
              identityId={identityId}
              onChanged={refetchActive}
              selectionMode={selectionMode}
              selected={selection.isSelected(p.playlistId)}
              onToggleSelected={() => selection.toggle(p.playlistId)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/** Rename follows the exact same isOwner/isShared branching as the playlist's own detail page
 * (see design.md Decision 2a): the owner can rename any of their own playlists, and any signed-in
 * user can rename a currently-shared playlist. Delete stays owner-only regardless of visibility —
 * deliberately not opened further, matching the detail page. */
function PlaylistListItem({
  playlist,
  identityId,
  onChanged,
  selectionMode,
  selected,
  onToggleSelected,
}: {
  playlist: Playlist;
  identityId: string | null | undefined;
  onChanged: () => void;
  selectionMode: boolean;
  selected: boolean;
  onToggleSelected: () => void;
}) {
  const isOwner = playlist.ownerId === identityId;
  const isShared = playlist.visibility === 'shared';
  const canRename = isOwner || isShared;

  const deleteMutation = useMutation({
    mutationFn: () => deletePlaylist(playlist.ownerId, playlist.playlistId),
    onSuccess: onChanged,
  });

  return (
    <ItemCard
      href={`/playlists/${playlist.playlistId}`}
      viewMode="list"
      hideThumbnail
      title={playlist.name}
      selectionMode={selectionMode}
      checkbox={isOwner ? { checked: selected, onChange: onToggleSelected } : undefined}
      onRename={
        canRename
          ? async (next) => {
              if (isOwner) {
                await renamePlaylist(playlist.ownerId, playlist.playlistId, next);
              } else {
                await editSharedPlaylist(playlist.ownerId, playlist.playlistId, { name: next });
              }
              onChanged();
            }
          : undefined
      }
      meta={
        <span className="muted">
          {playlist.videoIds.length} video{playlist.videoIds.length === 1 ? '' : 's'} · Updated{' '}
          {formatRelativeTime(playlist.updatedAt)}
          {isShared && (
            <span className="status-badge" style={{ marginLeft: 8 }}>
              Shared
            </span>
          )}
        </span>
      }
      actions={
        isOwner && (
          <ConfirmIconAction
            icon="🗑️"
            label="Delete playlist"
            confirmMessage="Delete this playlist? This cannot be undone."
            onConfirm={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
          />
        )
      }
    />
  );
}
